import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  BusinessProjectManagementService,
  DownloadService,
  LogService,
  QueryService,
} from '@pt/api-client';
import * as NgrxWidgetActions from '../actions/widget.actions';
import { upsertWidgetEnabled } from '../actions/widget.actions';
import * as NgrxWidgetSelectors from '../widget.selectors';
import {
  CommonDocumentEditorDetails,
  WorkspaceItemType,
} from '@primatoo-ms-organization';
import {
  ChapterPayload,
  DocumentEditorActionsType,
  DocumentEditorNotificationScope,
  DocumentEditorNotificationType,
  DocumentEditorType,
  DocumentInfo,
  LOG_INTERNAL_ID,
  NotificationType,
  TemplateAction,
  TemplateTrigger,
  UiWidget,
} from '@pt/pt-models';
import { WidgetUtils } from '../../widgets/widget.utils';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WidgetTextService } from '../../widgets/widget-text/widget-text.service';
import { WidgetCheckboxService } from '../../widgets/widget-checkbox/widget-checkbox.service';
import { WidgetAppAssessmentService } from '../../widgets/widget-app-assessment/widget-app-assessment.service';
import { WidgetAppSummaryService } from '../../widgets/widget-app-summary/widget-app-summary.service';
import { WidgetAttachmentService } from '../../widgets/widget-attachment/widget-attachment.service';
import { WidgetContactChooserService } from '../../widgets/widget-contact-chooser/widget-contact-chooser.service';
import { WidgetDataRetentionAndDeletionService } from '../../widgets/widget-data-retention-and-deletion/widget-data-retention-and-deletion.service';
import { WidgetHtmlService } from '../../widgets/widget-html/widget-html.service';
import { WidgetPreliminaryRiskAssessmentService } from '../../widgets/widget-preliminary-risk-assessment/widget-preliminary-risk-assessment.service';
import { WidgetRadiobuttonService } from '../../widgets/widget-radiobutton/widget-radiobutton.service';
import { WidgetRecipientChooserService } from '../../widgets/widget-recipient-chooser/widget-recipient-chooser.service';
import { WidgetRiskAssessmentService } from '../../widgets/widget-risk-assessment/widget-risk-assessment.service';
import { WidgetSimpleRadiobuttonService } from '../../widgets/widget-simple-radiobutton/widget-simple-radiobutton.service';
import { WidgetTreatmentDataInterestedService } from '../../widgets/widget-treatment-data-interested/widget-treatment-data-interested.service';
import { AbstractWidgetService } from '../../services/abstract-widget.service';
import { WidgetActionsUtils } from '../../utils/widget-actions-utils';
import { WidgetDpiaService } from '../../widgets/widget-dpia/widget-dpia.service';
import { WidgetMultiQuestionService } from '../../widgets/widget-multi-question/widget-multi-question.service';
import {
  PtCoreActions,
  PtCoreSelectors,
  PtWidgetModuleConfig,
  WidgetConfig,
} from '@pt/pt-core';
import { WidgetDateService } from '../../widgets/widget-date/widget-date.service';
import { DialogService } from '../../../services/dialog.service';
import * as AiAssessmentActions from '../actions/ai-assessment.actions';
import * as TreatmentActions from '../actions/treatment.actions';
import * as AppAssessmentActions from '../actions/app-assessment.actions';
import * as CovidAssessmentActions from '../actions/covid-assessment.actions';
import * as RiskAssessmentActions from '../actions/risk-assessment.actions';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { ListTemplateChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import { DocOperation } from 'it/primatoo/openapi/api/org/schemas/common/DocOperation';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { TriggerService } from '../../services/trigger.service';
import { EditorStoreService } from '../../services/editor-store.service';

import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';
import { Widget } from 'it/primatoo/openapi/api/org/schemas/common/docs/Widget';
import { PrimatooModule } from 'it/primatoo/openapi/api/org/schemas/common/PrimatooModule';
import { WidgetType } from 'it/primatoo/openapi/api/org/schemas/common/docs/WidgetType';
import { ChapterStatus } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterStatus';
import { DocAccess } from 'it/primatoo/openapi/api/org/schemas/common/DocAccess';
import { SetChapterStatusRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SetChapterStatusRequest';

@Injectable()
export class WidgetEffects {
  protected testId: number[] = LOG_INTERNAL_ID;

  private execTrigger(
    triggers: TemplateTrigger[],
    changedWidget: UiWidget,
    activeChapter: GetChapterResponse,
    activeChapterInfo: Chapter,
    docInfo: DocumentInfo,
    chapters: Chapter[],
    documentPermission: AvailableOperations,
    activeModules: PrimatooModule[],
    resetWarning?: TreatmentWarning[]
  ) {
    /**
     * Questo metodo divide le action dei trigger con target widget nello stesso capitolo detti syncAction con quelli relativi
     * ad un'altro capitolo detti asyncAction
     *
     * i syncAction e asyncAction a loro volota sono divisi in
     *  - action con query
     *  - action senza query
     */

    this.triggerStoreService.resetDisableWarning();
    this.triggerStoreService.resetActiveChapter();
    resetWarning?.forEach((w) => {
      this.triggerStoreService.setDisableWarning(w);
    });
    const request: Widget[] = [
      WidgetUtils.toWidgetRequest(changedWidget, activeChapterInfo.selector),
    ];
    this.notificationTriggerSave(null, docInfo.name);

    if (!triggers || triggers.length === 0) {
      this.store.dispatch(
        NgrxWidgetActions.saveWidgets({
          widgets: request,
          resetWarning: this.triggerStoreService.getDisabledWarning(),
          activeChapter: this.triggerStoreService.getActiveChapter(),
        })
      );
    } else {
      const activeChapterSelector = activeChapter.payload.selector;
      const syncAction: TemplateAction[] = [];
      const asyncAction: TemplateAction[] = [];

      triggers?.forEach((templateTrigger: TemplateTrigger) => {
        templateTrigger.actions?.forEach((action: TemplateAction) => {
          if (
            action.chapterSelector === undefined ||
            action.chapterSelector === activeChapterSelector
          ) {
            if (
              WidgetEffects.editableChapter(
                documentPermission?.operations,
                action.chapterSelector,
                activeModules
              )
            ) {
              syncAction.push(action);
            }
          } else {
            if (
              WidgetEffects.editableChapter(
                documentPermission.operations,
                action.chapterSelector,
                activeModules
              )
            ) {
              asyncAction.push(action);
            }
          }
        });
      });

      let syncRequest$: Observable<Widget[]> = of([]);
      if (syncAction.length > 0) {
        syncRequest$ = this.triggerService.execSyncTrigger(
          syncAction,
          changedWidget,
          docInfo,
          activeChapterInfo
        );
      }
      let asyncRequest$: Observable<Widget[]> = of([]);
      if (asyncAction.length > 0) {
        asyncRequest$ = this.triggerService.execAsyncTrigger(
          asyncAction,
          changedWidget,
          docInfo,
          activeChapterInfo,
          chapters
        );
      }
      forkJoin({
        syncRequest: syncRequest$,
        asyncRequest: asyncRequest$,
      }).subscribe(
        (response: { syncRequest: Widget[]; asyncRequest: Widget[] }) => {
          if (response.syncRequest?.length > 0) {
            response.syncRequest.forEach((r) => {
              if (r) {
                request.push(r);
              }
            });
          }

          if (response.asyncRequest?.length > 0) {
            response.asyncRequest.forEach((r) => {
              if (r) {
                request.push(r);
              }
            });
          }
          this.store.dispatch(
            NgrxWidgetActions.saveWidgets({
              widgets: request,
              resetWarning: this.triggerStoreService.getDisabledWarning(),
              activeChapter: this.triggerStoreService.getActiveChapter(),
            })
          );
        }
      );
    }
  }

  disableWarnings$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.disableWarnings),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([r, docInfo]) => {
          if (docInfo && docInfo.docId) {
            if (r?.request?.disabledWarnings?.length > 0) {
              const service: BusinessProjectManagementService =
                this.downloadService.getDocumentEditorTypeService(
                  docInfo.docType
                );
              service
                .disableWarnings(docInfo.docId, r.request)
                .subscribe(() => {
                  setTimeout(() => {
                    this.store.dispatch(NgrxWidgetActions.reloadWarnings());
                  }, 1000);
                });
            }
          } else {
            this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
          }
        })
      );
    },
    { dispatch: false }
  );

  reloadWarnings$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.reloadWarnings),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([, docInfo]) => {
          if (docInfo && docInfo.docId) {
            const service: BusinessProjectManagementService =
              this.downloadService.getDocumentEditorTypeService(
                docInfo.docType
              );
            service?.getEditorDetails(docInfo.docId).subscribe(
              (details: CommonDocumentEditorDetails) => {
                this.store.dispatch(
                  NgrxWidgetActions.reloadWarningsSuccess({
                    warnings: details.warnings,
                  })
                );
              },
              () => {
                // this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
              }
            );
          } else {
            this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
          }
        })
      );
    },
    { dispatch: false }
  );

  activeLiaChapter$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.activeLiaChapter),
        withLatestFrom(
          this.store.select(NgrxWidgetSelectors.getDocInfo),
          this.store.select(NgrxWidgetSelectors.getChapters),
          this.store.select(NgrxWidgetSelectors.getDocumentPermission),
          this.store.select(NgrxWidgetSelectors.getActiveChapterInfo),
          this.store.select(NgrxWidgetSelectors.lockEnabled)
        ),
        map(([, docInfo, chapters, permission]) => {
          if (docInfo && docInfo.docId && chapters && permission) {
            const liaChapter = chapters.find((chapter: Chapter) => {
              return chapter.selector === ChapterSelector.TREAT_LIA;
            });
            if (liaChapter) {
              switch (liaChapter.currentStatus) {
                case ChapterStatus.NOT_STARTED:
                  if (
                    (permission && permission.access === DocAccess.FULL) ||
                    permission.access === DocAccess.WRITE
                  ) {
                    if (
                      permission.operations.includes(
                        DocOperation.TREAT_EDIT_CHAPTER_LIA
                      )
                    ) {
                      this.store.dispatch(
                        NgrxWidgetActions.setChapterStatus({
                          docId: docInfo.docId,
                          chapterId: liaChapter.id,
                          request: {
                            chapter: ChapterSelector.TREAT_LIA,
                            status: ChapterStatus.WORKING,
                            completed: liaChapter.completed,
                          },
                        })
                      );

                      this.store.dispatch(
                        PtCoreActions.showNotification({
                          notification: {
                            type: NotificationType.success,
                            title: null,
                            message: 'Modificato lo stato del capitolo LIA.',
                          },
                          addToSession: false,
                        })
                      );
                      // this.notificationTriggerFailed(null, 'You do not have permission to modify the status of the \'LIA\' chapter.');
                    } else {
                      this.store.dispatch(
                        PtCoreActions.showNotification({
                          notification: {
                            type: NotificationType.error,
                            title: null,
                            message:
                              'Fallita modifica stato capitolo LIA. Permessi Mancanti!',
                          },
                          addToSession: false,
                        })
                      );
                    }
                  } else {
                    this.store.dispatch(
                      PtCoreActions.showNotification({
                        notification: {
                          type: NotificationType.error,
                          title: null,
                          message:
                            'Fallita modifica stato capitolo LIA. Permessi Mancanti!',
                        },
                        addToSession: false,
                      })
                    );
                    // this.notificationTriggerFailed(null, 'You do not have permission to modify the status of the \'LIA\' chapter.');
                  }
                  break;
                case ChapterStatus.WORKING:
                  console.warn('Chapter LIA: status WORKING');
                  this.store.dispatch(
                    NgrxWidgetActions.disableWarnings({
                      request: {
                        disabledWarnings: [TreatmentWarning.LI_SELECTED],
                      },
                    })
                  );
                  break;
                case ChapterStatus.TERMINATED:
                  console.warn('Chapter LIA: status TERMINATO');
                  this.store.dispatch(
                    NgrxWidgetActions.disableWarnings({
                      request: {
                        disabledWarnings: [TreatmentWarning.LI_SELECTED],
                      },
                    })
                  );
                  // this.store.dispatch(NgrxWidgetActions.disableWarnings({ request: { disabledWarnings: [TreatmentWarning.LI_SELECTED] } }));
                  break;
              }
            } else {
              console.warn('Chapter LIA not found');
            }

            // const service: BusinessProjectManagementService = this.downloadService.getDocumentEditorTypeService(docInfo.docType);
            // service.getDocumentTemplateChapters(docInfo.docId).subscribe(
            //   (listTemplateChapterResponse: ListTemplateChapterResponse) => {
            //     this.store.dispatch(NgrxWidgetActions.loadChaptersSuccess({ listTemplateChapter: listTemplateChapterResponse }));
            //   }, () => {
            //     this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
            //   }
            // );
          }
          // else {
          // this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
          // }
        })
      );
    },
    { dispatch: false }
  );

  /**
   * Lasciare false, lo utilizzo per testare l'inizializzazione dei widget ma lo abilito solo quando mi serve! @Giovanni
   */
  editorFirstInitialization$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.loadChapters),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([, docInfo]) => {
          if (docInfo && docInfo.docId) {
            const service: BusinessProjectManagementService =
              this.downloadService.getDocumentEditorTypeService(
                docInfo.docType
              );
            service.getDocumentTemplateChapters(docInfo.docId).subscribe(
              (listTemplateChapterResponse: ListTemplateChapterResponse) => {
                this.store.dispatch(
                  NgrxWidgetActions.loadChaptersSuccess({
                    listTemplateChapter: listTemplateChapterResponse,
                  })
                );
              },
              () => {
                this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
              }
            );
          } else {
            this.store.dispatch(NgrxWidgetActions.loadChaptersFailed());
          }
        })
      );
    },
    { dispatch: false }
  );

  updateTreatmentSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          RiskAssessmentActions.updateRiskAssessmentSuccess,
          TreatmentActions.updateTreatmentSuccess,
          TreatmentActions.updateTreatmentSuccess,
          AiAssessmentActions.updateAiAssessmentSuccess,
          AppAssessmentActions.updateAppAssessmentSuccess,
          CovidAssessmentActions.updateCovidAssessmentSuccess
        ),
        map((a) => a.response.docId),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([docId, docInfo]) => {
          if (docId === docInfo.docId) {
            this.store.dispatch(NgrxWidgetActions.reloadEditorDetails());
          }
        })
      );
    },
    { dispatch: false }
  );

  reloadEditorDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NgrxWidgetActions.reloadEditorDetails),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([, docInfo]) => {
          const service: BusinessProjectManagementService =
            this.downloadService.getDocumentEditorTypeService(docInfo.docType);
          service?.getEditorDetails(docInfo.docId).subscribe(
            (details: CommonDocumentEditorDetails) => {
              this.store.dispatch(
                NgrxWidgetActions.reloadWarningsSuccess({
                  warnings: details.warnings,
                })
              );
              this.editorStoreService.updateDateDocumentInfo(details.updatedAt);
              this.store.dispatch(
                NgrxWidgetActions.reloadEditorDetailsSuccess({ details })
              );
              // this.store.dispatch(NgrxWidgetActions.updateDocInfo({docInfo: service.updateDocInfoUtils(details, docInfo)}))
              this.store.dispatch(NgrxWidgetActions.resetEditorEditItemState());
            },
            () => {
              this.store.dispatch(
                NgrxWidgetActions.reloadEditorDetailsFailed()
              );
            }
          );
        })
      ),
    { dispatch: false }
  );

  loadChapter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NgrxWidgetActions.loadChapter),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([action, docInfo]) => {
          const service: BusinessProjectManagementService =
            this.downloadService.getDocumentEditorTypeService(docInfo.docType);
          service
            ?.getTreatmentTemplateChapterId(action.docId, action.chapter.id)
            .subscribe({
              next: (chapter: GetChapterResponse) => {
                this.triggerStoreService.updateChapterInfo(chapter);
                this.store.dispatch(
                  NgrxWidgetActions.loadChapterSuccess({ chapter: chapter })
                );
              },
              error: () => {
                this.store.dispatch(NgrxWidgetActions.loadChapterFailed());
              },
            });
        })
      ),
    { dispatch: false }
  );

  loadDocumentPermission$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.loadDocumentPermission),
        map((action: { docId: number; docType: DocumentEditorType }) => {
          const service: BusinessProjectManagementService =
            this.downloadService.getDocumentEditorTypeService(action.docType);
          if (service) {
            service.getDocUserPermission(action.docId).subscribe(
              (operations) => {
                this.store.dispatch(
                  NgrxWidgetActions.loadDocumentPermissionSuccess({
                    operations,
                  })
                );
              },
              () => {
                this.store.dispatch(
                  NgrxWidgetActions.loadDocumentPermissionFailed()
                );
              }
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  loadChapterSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.loadChapterSuccess),
        map((action) => action?.chapter),
        map((chapter) => {
          this.store.dispatch(NgrxWidgetActions.initializeChapter({ chapter }));
        })
      );
    },
    { dispatch: false }
  );

  initializeChapter$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.initializeChapter),
        map((action) => action?.chapter),
        withLatestFrom(
          this.store.select(NgrxWidgetSelectors.getDocInfo),
          this.store.select(NgrxWidgetSelectors.getDocumentPermission),
          this.store.select(NgrxWidgetSelectors.getActiveChapterInfo),
          this.store.select(PtCoreSelectors.activeModules)
        ),
        map(
          ([chapter, docInfo, docPermission, activeChapter, activeModule]) => {
            const widgetsFromChapter: UiWidget[] =
              WidgetUtils.getWidgetsFromChapter(chapter);
            const payload: ChapterPayload = chapter?.payload;
            const widgetNotSaved: UiWidget[] =
              WidgetUtils.getWidgetNotSavedFromChapterPayload(payload);
            const overrideWidget: UiWidget[] = [];

            const hasPermission: boolean = WidgetEffects.editableChapter(
              docPermission?.operations,
              payload.selector,
              activeModule
            );
            this.triggerStoreService.setWidgets(widgetsFromChapter);

            if (hasPermission) {
              if (widgetNotSaved?.length > 0) {
                const actionIndexWithQuery = [];
                const actionIndexWithoutQuery = [];
                const observableQuery: Observable<unknown>[] = [];
                const templateInitializationActions: TemplateAction[] =
                  payload?.initialization;

                templateInitializationActions?.forEach(
                  (action: TemplateAction, index: number) => {
                    if (action?.args?.query) {
                      observableQuery.push(
                        this.queryService.resolve(action.args.query)
                      );
                      actionIndexWithQuery.push(index);
                    } else {
                      actionIndexWithoutQuery.push(index);
                    }
                  }
                );

                actionIndexWithoutQuery.forEach((i) => {
                  const currentTemplateAction: TemplateAction =
                    templateInitializationActions[i] as TemplateAction;
                  const widget: UiWidget = widgetNotSaved.find(
                    (w: UiWidget) =>
                      w.internalId === currentTemplateAction.widgetId
                  );
                  if (widget) {
                    switch (currentTemplateAction.action) {
                      case DocumentEditorActionsType.INITIALIZATION:
                        const service: AbstractWidgetService | null =
                          this.getWidgetService(widget.type);
                        if (service) {
                          const upsertWidget: UiWidget = service.initialization(
                            chapter,
                            widgetNotSaved,
                            currentTemplateAction.widgetId,
                            currentTemplateAction.action,
                            currentTemplateAction.args,
                            null
                          );
                          if (upsertWidget) {
                            this.notificationInitializationInfo(
                              upsertWidget,
                              docInfo.name,
                              currentTemplateAction.action
                            );
                            overrideWidget.push(upsertWidget);
                          }
                        }
                        break;
                      case DocumentEditorActionsType.VALUE:
                        overrideWidget.push({
                          ...widget,
                          value: currentTemplateAction.value,
                        });
                        break;
                      default:
                        console.warn(
                          'Not supported initialization action ',
                          currentTemplateAction.action
                        );
                    }
                  }
                });

                if (observableQuery.length > 0) {
                  forkJoin([
                    ...observableQuery.map((query, index) =>
                      query.pipe(
                        map((response) => {
                          return response;
                        }),
                        catchError(() => {
                          const currentTemplateAction: TemplateAction =
                            templateInitializationActions[
                              index
                            ] as TemplateAction;
                          const widget = widgetNotSaved?.find(
                            (w) =>
                              w.internalId === currentTemplateAction.widgetId
                          );
                          this.notificationInitializationFailed(
                            widget,
                            docInfo.name,
                            currentTemplateAction.action
                          );
                          return EMPTY;
                        })
                      )
                    ),
                  ]).subscribe(
                    (response) => {
                      response?.forEach((queryResponse, index) => {
                        if (queryResponse) {
                          const actionIndex = actionIndexWithQuery[index];
                          const currentTemplateAction: TemplateAction =
                            templateInitializationActions[
                              actionIndex
                            ] as TemplateAction;
                          const widget = widgetNotSaved.find(
                            (w) =>
                              w.internalId === currentTemplateAction.widgetId
                          );
                          if (widget) {
                            const service = this.getWidgetService(widget.type);
                            if (service) {
                              const upsertedWidget = service.initialization(
                                chapter,
                                widgetNotSaved,
                                currentTemplateAction.widgetId,
                                currentTemplateAction.action,
                                currentTemplateAction.args,
                                queryResponse
                              );
                              if (upsertedWidget) {
                                this.notificationInitializationInfo(
                                  upsertedWidget,
                                  docInfo.name,
                                  currentTemplateAction.action
                                );
                                overrideWidget.push(upsertedWidget);
                              }
                            }
                          }
                        }
                      });
                      this.store.dispatch(
                        NgrxWidgetActions.saveWidgets({
                          widgets: this.toWidgetEnriched(
                            widgetNotSaved,
                            overrideWidget,
                            activeChapter.selector
                          ),
                          initialization: true,
                        })
                      );
                    },
                    () => {
                      this.store.dispatch(
                        NgrxWidgetActions.initializeChapterFailed()
                      );
                    }
                  );
                } else {
                  widgetNotSaved.forEach((widget) => {
                    // Effetto collaterale: notifica inizializzazione
                    this.notificationInitializationInfo(
                      widget,
                      docInfo.name,
                      null
                    );
                  });

                  this.store.dispatch(
                    NgrxWidgetActions.saveWidgets({
                      widgets: this.toWidgetEnriched(
                        widgetNotSaved,
                        overrideWidget,
                        activeChapter.selector
                      ),
                      initialization: true,
                    })
                  );
                }
              } else {
                this.notificationInitializationSuccess(
                  null,
                  docInfo.name,
                  null
                );
                this.store.dispatch(
                  NgrxWidgetActions.initializeChapterSuccess({
                    chapter: chapter,
                  })
                );
              }
            } else {
              this.notificationInitializationSuccess(null, docInfo.name, null);
              this.store.dispatch(
                NgrxWidgetActions.initializeChapterSuccess({ chapter: chapter })
              );
            }
          }
        )
      );
    },
    { dispatch: false }
  );

  saveWidgets$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.saveWidgets),
        withLatestFrom(
          this.store.select(NgrxWidgetSelectors.getWidgets),
          this.store.select(NgrxWidgetSelectors.getDocInfo),
          this.store.select(NgrxWidgetSelectors.getActiveChapterInfo),
          this.store.select(NgrxWidgetSelectors.lockEnabled)
        ),
        map(([saveWidgets, widgets, docInfo, chapterInfo]) => {
          const isInitialization = saveWidgets.initialization;
          if (saveWidgets.widgets.length > 0) {
            const request: UpsertWidgetsRequest = {
              widgets: saveWidgets.widgets,
              ghostOperation: isInitialization,
            };
            // WidgetActionsUtils.generateWidgetsUpsertRequest(saveWidgets?.widgets, chapterInfo.selector);
            // Se la chiamata di upsert mi scoppia mi premuro di mettere saved a true. Il non farlo genera un ricaricamento normale di tutt
            // il saved se è a false fa partire la modale di controllo delle warning e ritenta il salvataggio e così si innesca un loop
            const widgetForgetSavedTrue =
              WidgetActionsUtils.forceSetSavedTrue(widgets);

            const service: BusinessProjectManagementService =
              this.downloadService.getDocumentEditorTypeService(
                docInfo.docType
              );
            if (service) {
              service.upsertWidgets(docInfo.docId, request).subscribe(
                () => {
                  if (this.logService.isLoggable('DEBUG')) {
                    console.groupCollapsed('[DEBUG] Widget Aggiornati');
                    request.widgets.forEach((widget) => {
                      console.log(`${widget.internalId}-${widget.type}`);
                    });
                    console.groupEnd();
                  }

                  this.store.dispatch(upsertWidgetEnabled({ request }));
                  this.triggerStoreService.upsertWidgetFromRequest(request);
                  if (docInfo.userCheck) {
                    this.store.dispatch(
                      NgrxWidgetActions.userCheckTrueInDraftDocument({
                        docId: docInfo.docId,
                      })
                    );
                  }

                  if (saveWidgets.resetWarning) {
                    this.store.dispatch(
                      NgrxWidgetActions.disableWarnings({
                        request: { disabledWarnings: saveWidgets.resetWarning },
                      })
                    );
                  }

                  if (saveWidgets.activeChapter) {
                    saveWidgets.activeChapter.forEach((chapter) => {
                      if (chapter === ChapterSelector.TREAT_LIA) {
                        this.store.dispatch(
                          NgrxWidgetActions.activeLiaChapter()
                        );
                      } else {
                        console.warn('Unsupported active chatper ', chapter);
                      }
                    });
                  }

                  service
                    .getTreatmentTemplateChapterId(
                      docInfo.docId,
                      chapterInfo?.id
                    )
                    .subscribe(
                      (chapter: GetChapterResponse) => {
                        this.triggerStoreService.updateChapterInfo(chapter);
                        const widgets: UiWidget[] =
                          WidgetActionsUtils.getWidgetFromChapter(
                            chapter,
                            request?.widgets?.map((w) => {
                              return w.internalId;
                            })
                          );

                        if (saveWidgets.initialization) {
                          this.store.dispatch(
                            NgrxWidgetActions.initializeChapterSuccess({
                              chapter,
                            })
                          );
                        } else {
                          this.store.dispatch(
                            NgrxWidgetActions.reloadWidgetAfterSave({ widgets })
                          );
                        }
                      },
                      () => {
                        if (saveWidgets.initialization) {
                          this.store.dispatch(
                            NgrxWidgetActions.initializeChapterFailed()
                          );
                        } else {
                          this.store.dispatch(
                            NgrxWidgetActions.reloadWidgetAfterSave({ widgets })
                          );
                        }
                      }
                    );
                },
                () => {
                  // questa azione mi rimette a posto lo stato globale. Succedeva che senza il widget preliminary risk restava in edit mode
                  if (saveWidgets.initialization) {
                    widgets?.forEach((w) => {
                      this.notificationInitializationFailed(
                        w,
                        docInfo.name,
                        null
                      );
                    });
                    this.store.dispatch(
                      NgrxWidgetActions.initializeChapterFailed()
                    );
                  } else {
                    this.store.dispatch(
                      NgrxWidgetActions.reloadWidgetAfterSave({
                        widgets: widgetForgetSavedTrue,
                      })
                    );
                  }
                }
              );
            }
          }
        })
      );
    },
    { dispatch: false }
  );

  execTriggerWidget$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.execTriggerWidget),
        withLatestFrom(
          this.store.select(NgrxWidgetSelectors.getTrigger),
          this.store.select(NgrxWidgetSelectors.getChapter),
          this.store.select(NgrxWidgetSelectors.getDocInfo),
          this.store.select(NgrxWidgetSelectors.getChapters),
          this.store.select(NgrxWidgetSelectors.getDocumentPermission),
          this.store.select(NgrxWidgetSelectors.getActiveChapterInfo),
          this.store.select(PtCoreSelectors.activeModules)
        ),
        map(
          ([
            a,
            triggers,
            activeChapter,
            docInfo,
            chapters,
            documentPermission,
            activeChapterInfo,
            activeModules,
          ]) => {
            const currentTriggers: TemplateTrigger[] = triggers?.filter(
              (t: TemplateTrigger) => t.widgetId == a.widget.internalId
            );
            this.execTrigger(
              currentTriggers,
              a.widget,
              activeChapter,
              activeChapterInfo,
              docInfo,
              chapters,
              documentPermission,
              activeModules,
              a.resetWarning
            );
          }
        )
      );
    },
    { dispatch: false }
  );

  userCheckTrueInPublishedDocument$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.userCheckTrueInPublishedDocument),
        withLatestFrom(
          this.store.select(NgrxWidgetSelectors.getWidgetsStatus),
          this.store.select(NgrxWidgetSelectors.getDocInfo)
        ),
        map(([action, status, docInfo]) => {
          const doc = action.document;
          // const service: BusinessProjectManagementService = this.downloadService.getDocumentEditorTypeService(docInfo.docType);
          this.dialogService
            .runUserCheckDialog(doc, status)
            .subscribe((success) => {
              this.dialogService.closeAllDialog();
              switch (success) {
                case 'CLOSE':
                  this.store.dispatch(
                    PtCoreActions.navigateBack({
                      hop: 1,
                      queryParams: { id: doc.docId, opened: true },
                    })
                  );
                  break;
                case 'CONFIRM':
                  console.error('Confirm User Check.');
                  // service.userChecked(doc.docId).subscribe(() => {
                  //   this.store.dispatch(NgrxWidgetActions.refreshChapterInfo());
                  // }, () => {
                  //   this.store.dispatch(NgrxWidgetActions.refreshChapterInfo());
                  // })
                  break;
                case 'REOPEN':
                  this.store.dispatch(
                    NgrxWidgetActions.reopenDocument({
                      id: doc.docId,
                      docType: docInfo.docType,
                    })
                  );
                  break;
              }
            });
        })
      );
    },
    { dispatch: false }
  );
  userCheckTrueInDraftDocument$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NgrxWidgetActions.userCheckTrueInDraftDocument),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([action, docInfo]) => {
          const docId = action.docId;
          const service: BusinessProjectManagementService =
            this.downloadService.getDocumentEditorTypeService(docInfo.docType);
          return service.userChecked(docId).subscribe(
            () => {
              this.store.dispatch(NgrxWidgetActions.reloadEditorDetails());
            },
            () => {
              this.store.dispatch(NgrxWidgetActions.reloadEditorDetails());
            }
          );
        })
      );
    },
    { dispatch: false }
  );
  reopenDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NgrxWidgetActions.reopenDocument),
      switchMap((action) => {
        switch (action.docType) {
          case WorkspaceItemType.APP_ASSESSMENT:
          case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
            return [
              AppAssessmentActions.reopenAppAssessment({ id: action.id }),
            ];
          case WorkspaceItemType.AI_ASSESSMENT:
          case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
            return [AiAssessmentActions.reopenAiAssessment({ id: action.id })];
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
            return [
              RiskAssessmentActions.reopenRiskAssessment({ id: action.id }),
            ];
          case WorkspaceItemType.COVID_ASSESSMENT:
          case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
            return [
              CovidAssessmentActions.reopenCovidAssessment({ id: action.id }),
            ];
          default:
            return [TreatmentActions.reopenTreatment({ id: action.id })];
        }
      })
    );
  });
  setCompleteDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NgrxWidgetActions.setChapterStatus),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([action, docInfo]) => {
          const service: BusinessProjectManagementService =
            this.downloadService.getDocumentEditorTypeService(docInfo.docType);
          const request: SetChapterStatusRequest = action.request;
          if (this.logService.isLoggable('DEBUG')) {
            console.groupCollapsed('[DEBUG] Set Chapter Status');
            console.log('SetChapterStatus ', request);
            console.log(
              'updatedAt ',
              request.updatedAt
                ? new Date(request.updatedAt)
                : request.updatedAt
            );
            console.groupEnd();
          }
          service
            .setChapterStatus(action.docId, action.chapterId, action.request)
            .subscribe(() => {
              if (
                action.request.chapter === ChapterSelector.TREAT_LIA &&
                action.request.status !== ChapterStatus.NOT_STARTED
              ) {
                this.store.dispatch(
                  NgrxWidgetActions.disableWarnings({
                    request: {
                      disabledWarnings: [TreatmentWarning.LI_SELECTED],
                    },
                  })
                );
              }
              this.store.dispatch(
                NgrxWidgetActions.setChapterStatusSuccess(action)
              );
            });
        })
      ),
    { dispatch: false }
  );

  setChapterStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NgrxWidgetActions.setChapterStatusSuccess),
        withLatestFrom(this.store.select(NgrxWidgetSelectors.getDocInfo)),
        map(([action, docInfo]) => {
          const service: BusinessProjectManagementService =
            this.downloadService.getDocumentEditorTypeService(docInfo.docType);
          service
            ?.getTreatmentTemplateChapterId(action.docId, action.chapterId)
            .subscribe({
              next: (chapter: GetChapterResponse) => {
                this.triggerStoreService.updateChapterInfo(chapter);
              },
            });
        })
      ),
    { dispatch: false }
  );

  moveSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TreatmentActions.moveTreatmentSuccess,
          RiskAssessmentActions.moveRiskAssessmentSuccess,
          AiAssessmentActions.moveAiAssessmentSuccess,
          AppAssessmentActions.moveAppAssessmentSuccess,
          CovidAssessmentActions.moveCovidAssessmentSuccess
        ),
        map(() => {
          this.store.dispatch(NgrxWidgetActions.reloadEditorDetails());
        })
      ),
    { dispatch: false }
  );

  constructor(
    @Inject(WidgetConfig) private config: PtWidgetModuleConfig,
    private actions$: Actions,
    private triggerService: TriggerService,
    private triggerStoreService: EditorStoreService,
    private queryService: QueryService,
    private downloadService: DownloadService,
    private translateService: TranslateService,
    private widgetTextService: WidgetTextService,
    private widgetCheckboxService: WidgetCheckboxService,
    private widgetAppAssessmentService: WidgetAppAssessmentService,
    private widgetAppSummaryService: WidgetAppSummaryService,
    private widgetAttachmentService: WidgetAttachmentService,
    private widgetContactChooserService: WidgetContactChooserService,
    private widgetDataRetentionAndDeletionService: WidgetDataRetentionAndDeletionService,
    private widgetHtmlService: WidgetHtmlService,
    private widgetMultiQuestionService: WidgetMultiQuestionService,
    private widgetPreliminaryRiskAssessmentService: WidgetPreliminaryRiskAssessmentService,
    private widgetRadioButtonService: WidgetRadiobuttonService,
    private widgetRecipientChooserService: WidgetRecipientChooserService,
    private widgetRiskAssessmentService: WidgetRiskAssessmentService,
    private widgetSimpleRadioButtonService: WidgetSimpleRadiobuttonService,
    private widgetTreatmentDataInterestedService: WidgetTreatmentDataInterestedService,
    private widgetDpiaService: WidgetDpiaService,
    private widgetDateService: WidgetDateService,
    private dialogService: DialogService,
    private editorStoreService: EditorStoreService,
    private logService: LogService,
    private store: Store
  ) {}

  /**
   * Aggiungere i servizi per ogni nuovo tipo di widget
   * @param widgetType
   * @private
   */
  private getWidgetService(
    widgetType?: WidgetType
  ): AbstractWidgetService | null {
    if (widgetType) {
      switch (widgetType) {
        case WidgetType.TEXT:
          return this.widgetTextService;
        case WidgetType.CHECKBOX:
          return this.widgetCheckboxService;
        case WidgetType.APP_ASSESSMENT:
          return this.widgetAppAssessmentService;
        case WidgetType.APP_SUMMARY:
          return this.widgetAppSummaryService;
        case WidgetType.ATTACHMENT:
          return this.widgetAttachmentService;
        case WidgetType.CONTACT_CHOOSER:
          return this.widgetContactChooserService;
        case WidgetType.DATA_RETENTION_AND_DELETION:
          return this.widgetDataRetentionAndDeletionService;
        case WidgetType.HTML:
          return this.widgetHtmlService;
        case WidgetType.MULTI_QUESTION:
          return this.widgetMultiQuestionService;
        case WidgetType.PRELIMINARY_RISK_ASSESSMENT:
          return this.widgetPreliminaryRiskAssessmentService;
        case WidgetType.RADIO_BUTTON:
          return this.widgetRadioButtonService;
        case WidgetType.RECIPIENT_CHOOSER:
          return this.widgetRecipientChooserService;
        case WidgetType.RISK_ASSESSMENT:
          return this.widgetRiskAssessmentService;
        case WidgetType.SIMPLE_RADIO_BUTTON:
          return this.widgetSimpleRadioButtonService;
        case WidgetType.TREATMENT_DATA_INTERESTED:
          return this.widgetTreatmentDataInterestedService;
        case WidgetType.DPIA:
          return this.widgetDpiaService;
        case WidgetType.DATE:
          return this.widgetDateService;
      }
    }
    return null;
  }

  private notificationTriggerSave(widget: UiWidget, name: string) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      null,
      this.translateService.stream('WIDGET_WARNING.SAVE_WIDGET'),
      DocumentEditorNotificationType.INFO,
      DocumentEditorNotificationScope.TRIGGER
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  private notificationTriggerSuccess(widget: UiWidget, name: string) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      null,
      this.translateService.stream('WIDGET_WARNING.TRIGGER_SUCCESS'),
      DocumentEditorNotificationType.INFO,
      DocumentEditorNotificationScope.TRIGGER
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  private notificationTriggerFailed(widget: UiWidget, name: string) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      null,
      this.translateService.stream('WIDGET_WARNING.TRIGGER_FAILED'),
      DocumentEditorNotificationType.ERROR,
      DocumentEditorNotificationScope.TRIGGER
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  private notificationInitializationInfo(
    widget: UiWidget,
    name: string,
    action: DocumentEditorActionsType
  ) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      action,
      of(widget.label),
      DocumentEditorNotificationType.INFO,
      DocumentEditorNotificationScope.INITIALIZATION
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  private notificationInitializationFailed(
    widget: UiWidget,
    name: string,
    action: DocumentEditorActionsType
  ) {
    // Ho commentato questa notifica su indicazione di Greta issue https://gitlab.gfm.cloud/primatoo/primatoo/-/issues/1807 by GEG
    // const data = WidgetUtils.generateWidgetNotification(
    //   widget ? widget.internalId : 0,
    //   name,
    //   action,
    //   this.translateService.stream('MESSAGE.INITIALIZATION_WIDGET_FAILED'),
    //   DocumentEditorNotificationType.ERROR,
    //   DocumentEditorNotificationScope.INITIALIZATION);
    // this.store.dispatch(NgrxWidgetActions.emitDocumentEditorNotification({ data }));
  }

  private notificationInitializationSuccess(
    widget: UiWidget,
    name: string,
    action: DocumentEditorActionsType
  ) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      action,
      this.translateService.stream('MESSAGE.INITIALIZATION_WIDGET_SUCCESS'),
      DocumentEditorNotificationType.SUCCESS,
      DocumentEditorNotificationScope.INITIALIZATION
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  public static editableChapter(
    operations: Array<DocOperation>,
    chapterSelector?: ChapterSelector,
    activeModule?: PrimatooModule[]
  ) {
    let editable = false;
    switch (chapterSelector) {
      case undefined:
      case ChapterSelector.BASIC:
      case ChapterSelector.STANDARD:
        editable = operations?.includes(DocOperation.EDIT_CHAPTER_CUSTOM);
        break;
      case ChapterSelector.TREAT_TREAT:
        editable = operations?.includes(DocOperation.TREAT_EDIT_CHAPTER_TREAT);
        break;
      case ChapterSelector.TREAT_DPIA:
      case ChapterSelector.TREAT_RA:
      case ChapterSelector.TREAT_CHECK:
        editable = operations?.includes(
          DocOperation.TREAT_EDIT_CHAPTER_CHECK_RA_DPIA
        );
        break;
      case ChapterSelector.TREAT_LIA:
        if (activeModule?.includes(PrimatooModule.LIA)) {
          editable = operations?.includes(DocOperation.TREAT_EDIT_CHAPTER_LIA);
        }
        break;
    }

    return editable;
  }

  private testWidgetId(request: Widget[]) {
    request.forEach((r) => {
      if (this.testId.includes(r.internalId)) return true;
    });
    return false;
  }

  private toWidgetEnriched(
    uiWidgets: UiWidget[],
    overrideWidgets: UiWidget[],
    chapterSelector: ChapterSelector
  ): Widget[] {
    const out: Widget[] = [];
    uiWidgets.forEach((w: UiWidget) => {
      const override = overrideWidgets.find(
        (ow) => ow.internalId === w.internalId
      );
      if (override) {
        out.push(WidgetUtils.toWidgetRequest(override, chapterSelector));
      } else {
        out.push(WidgetUtils.toWidgetRequest(w, chapterSelector));
      }
    });
    return out;
  }
}
